import React, { useContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import styled, { css } from 'styled-components';
import generateId from 'src/lib/generateId';

import { AddButton } from 'src/presentation/components/pages/shared/EditorSectionLayout';
import { ButtonSet as ButtonSetType } from 'src/models/Sidekick/SidekickModuleSpec';
import DraggableList, {
  DragPointer,
  DraggableListContent,
} from 'src/presentation/components/pages/shared/DraggableList';
import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import LightningIcon from '@ad-scripts/presentation/components/common/icons/stroke/lightning-stroke';
import RemoveStrokeIcon from '@ad-scripts/presentation/components/common/icons/stroke/remove-stroke';
import { StyledColumn } from 'src/presentation/components/pages/sidekick/threads/components/Layout';

type ButtonSetProps = {
  attr: string;
  description?: string;
  disabled: boolean;
};

export default function ButtonSet({ attr, disabled, description }: ButtonSetProps) {
  const { workingState, updateState, onBlur } = useContext(FormContext);
  const [buttonSet, setButtonSet] = useState<ButtonSetType>({ regular: [], smart: [] });

  useEffect(() => {
    const state = get(workingState, attr, {}) as any as ButtonSetType;
    const clonedButtons = cloneDeep(state);

    clonedButtons.regular.filter(button => !button.id).forEach(button => (button.id = generateId()));
    clonedButtons.smart.filter(button => !button.id).forEach(button => (button.id = generateId()));

    setButtonSet(clonedButtons);
  }, [attr, workingState]);

  const updateWorkingState = (newState: any) => {
    updateState(set({}, attr, newState), { replaceArrayValues: true });
    onBlur && onBlur({ currentState: set({}, attr, newState) });
  };

  const handleChange = (idx: number, isSmart: boolean) => (name: string) => {
    const clonedButtons = cloneDeep(buttonSet);
    const button = isSmart ? clonedButtons.smart[idx] : clonedButtons.regular[idx];
    button.label = name;

    setButtonSet(clonedButtons);
  };

  const onInputBlur = () => updateWorkingState(buttonSet);

  const handleSmartButtonClick = (idx: number, isSmart: boolean) => (ev: Event) => {
    ev.preventDefault();
    if (disabled) {
      return;
    }

    const clonedButtons = cloneDeep(buttonSet);
    const button = isSmart ? buttonSet.smart[idx] : buttonSet.regular[idx];
    isSmart ? clonedButtons.smart.splice(idx, 1) : clonedButtons.regular.splice(idx, 1);
    isSmart ? clonedButtons.regular.push(button) : clonedButtons.smart.push(button);

    updateWorkingState(clonedButtons);
  };

  const onAddButtonClick = () => {
    const clonedButtons = cloneDeep(buttonSet);
    clonedButtons.regular.push({ label: '', id: generateId() });

    setButtonSet(clonedButtons);
    updateState(set({}, attr, clonedButtons), { replaceArrayValues: true });
  };

  const handleDelete = (idx: number, isSmart: boolean) => {
    const clonedButtons = cloneDeep(buttonSet);
    isSmart ? clonedButtons.smart.splice(idx, 1) : clonedButtons.regular.splice(idx, 1);

    updateWorkingState(clonedButtons);
  };

  const handleDragEnd = ({ source, destination }: { source: { index: number }; destination: { index: number } }) => {
    const clonedButtons = cloneDeep(buttonSet);
    const [removed] = clonedButtons.regular.splice(source.index, 1);
    clonedButtons.regular.splice(destination.index, 0, removed);

    updateWorkingState(clonedButtons);
  };

  const getItemStyle = (isDragging, draggableStyle) => {
    if (!isDragging) {
      return draggableStyle;
    }
    return {
      userSelect: 'none',
      background: isDragging ? 'lightgreen' : 'grey',
      ...draggableStyle,
      left: 'unset',
      right: 285,
    };
  };

  const renderSmartButtons = () => {
    if (buttonSet.smart.length === 0) {
      return null;
    }

    return buttonSet.smart.map((button, idx) => (
      <div key={button.id}>
        <RowContainer>
          <InputField
            attr=""
            disabled={disabled}
            value={button.label}
            onChange={handleChange(idx, true)}
            onBlur={onInputBlur}
          />
          <div>
            <StyledTooltip message="Unassign Smart Button">
              <StyledSmartButton disabled={disabled} onClick={handleSmartButtonClick(idx, true)} $isSmartButton={true}>
                <SmartButton viewBox="2 0 19 24" width={16} />
              </StyledSmartButton>
            </StyledTooltip>
          </div>
          {!disabled && (
            <StyledTooltip message="Delete">
              <StyledTrashIcon width={16} onClick={() => handleDelete(idx, true)} />
            </StyledTooltip>
          )}
        </RowContainer>
      </div>
    ));
  };

  const renderRegularButtons = () => (
    <DraggableListContent onDragEnd={handleDragEnd}>
      <DraggableList
        disabled={false}
        items={buttonSet.regular}
        renderRow={({ index: idx, item: row, provided, snapshot }) => (
          <RowContainer
            key={row.id}
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
          >
            <StyledTooltip message="Drag to Reorder">
              <DragPointer id={`drag-handler-${row.id}`} isDragging={snapshot.isDragging} provided={provided} />
            </StyledTooltip>
            <InputField
              attr=""
              disabled={disabled}
              value={row.label}
              onChange={handleChange(idx, false)}
              onBlur={onInputBlur}
            />
            <div>
              <StyledTooltip message="Assign Smart Button">
                <StyledSmartButton
                  disabled={disabled}
                  onClick={handleSmartButtonClick(idx, false)}
                  $isSmartButton={false}
                >
                  <SmartButton viewBox="2 0 19 24" width={16} />
                </StyledSmartButton>
              </StyledTooltip>
            </div>
            {!disabled && (
              <StyledTooltip message="Delete">
                <StyledTrashIcon width={16} onClick={() => handleDelete(idx, false)} />
              </StyledTooltip>
            )}
          </RowContainer>
        )}
        droppableId="button-set-droppable"
        type="button-set"
      />
    </DraggableListContent>
  );

  return (
    <>
      <ControlLabelWrapper>
        <ControlLabel>{prompt}</ControlLabel>
      </ControlLabelWrapper>
      <StyledControlContainer>
        <ControlDescription>{description}</ControlDescription>
      </StyledControlContainer>
      <StyledColumn>
        {renderSmartButtons()}
        {renderRegularButtons()}
        {!disabled && (
          <AddButtonWrapper>
            <AddButton text="Add Button" onClick={onAddButtonClick} />
          </AddButtonWrapper>
        )}
      </StyledColumn>
    </>
  );
}

const SmartButton = styled(LightningIcon)`
  cursor: pointer;
  margin-right: 4px;
  stroke: ${p => p.theme.colors.gray600};
`;

const ActiveSmartButton = css`
  background: ${p => (p.$isSmartButton ? p.theme.colors.green400 : p.theme.colors.green100)};
  ${SmartButton} {
    stroke: ${p => p.theme.colors.white};
  }
`;

const StyledSmartButton = styled.div`
  ${SmartButton};
  align-content: space-evenly;
  border-radius: 25px;
  margin-left: 4px;
  padding-left: 2px;
  padding-right: 2px;
  ${p => p.$isSmartButton && ActiveSmartButton}
`;

const StyledTrashIcon = styled(RemoveStrokeIcon)`
  fill: ${p => p.theme.colors.gray600};
  cursor: pointer;
  margin-left: 8px;
  visibility: hidden;
`;

const RowContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 8px;
  &:hover {
    background: ${p => p.theme.colors.green100};
    svg {
      visibility: visible;
    }
  }
`;

const StyledTooltip = styled(Tooltip)`
  &:hover {
    background: ${p => p.theme.colors.gray100};
  }
`;

const AddButtonWrapper = styled.div`
  margin-top: 12px;
`;

const StyledControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${p => (p.expand ? 'flex: 1 1 auto' : '')};
  ${p => (p.preventContentFromOverflow ? 'min-width: 0' : '')};
`;

export const ControlDescription = styled.div`
  align-self: flex-start;
  font-size: ${p => p.theme.fontSize.base};
  color: ${p => p.theme.colors.gray700};
  line-height: 1.6;
  margin-bottom: ${p => p.theme.spacing.stackSmall};
  min-height: 18px;
`;

export const ControlLabelWrapper = styled.div`
  display: flex;
  font-size: ${p => p.theme.fontSize.base};
`;

export const ControlLabel = styled.label`
  align-self: flex-start;
  font-weight: bolder;
  margin-bottom: ${p => p.theme.spacing.stackSmall};
  min-height: 18px;
`;
